import React, { useState, useEffect, useMemo } from 'react'
import { Box, Typography, Divider, Stack, Paper, Radio } from '@mui/material'
import { styled } from '@mui/material/styles'
import theme from 'core/Theme'
import { useTranslation } from 'react-i18next'
import { KButton } from 'components/core/KButton'
import { CreditCardEditDialog } from './CreditCardEditDialog'
import { useCurrentUser } from 'hooks/AuthHooks'
import { KCardIcon } from 'components/core/KCardIcon'
import { StoredPaymentMethod } from 'models'

const CardItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  cursor: 'pointer',
  position: 'relative',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}))

const CardLogo = styled(Box)(({ theme }) => ({
  width: 60,
  height: 40,
  marginRight: theme.spacing(2.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const DefaultLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  marginLeft: 'auto',
  fontSize: theme.typography.body1.fontSize,
}))

export interface CreditCardListProps {
  onSelectCard?: (card: StoredPaymentMethod) => void
  selectionMode?: boolean
  selectedCard?: StoredPaymentMethod
  hideActionButtons?: boolean
}

export const CreditCardList = ({
  onSelectCard,
  selectionMode = false,
  selectedCard,
  hideActionButtons = false,
}: CreditCardListProps) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [currentSelectedCard, setCurrentSelectedCard] = useState<
    StoredPaymentMethod | undefined
  >(selectedCard)

  const cards = useMemo(
    () => currentUser?.paymentMethods || [],
    [currentUser?.paymentMethods],
  )

  const isOnlyDefaultCard = useMemo(() => {
    return cards.length === 1 && cards[0]?.default === true
  }, [cards])

  useEffect(() => {
    if (selectedCard) {
      setCurrentSelectedCard(selectedCard)
    }
  }, [selectedCard])

  const handleAddClick = () => {
    setCurrentSelectedCard(undefined)
    setEditDialogOpen(true)
  }

  const handleCardClick = async (card: StoredPaymentMethod) => {
    if (selectionMode) {
      handleSelectCard(card)
    } else {
      setCurrentSelectedCard(card)
      setEditDialogOpen(true)
    }
  }

  const handleDialogCancel = () => {
    setEditDialogOpen(false)
    if (!selectionMode) {
      setCurrentSelectedCard(undefined)
    }
  }

  const handleSelectCard = (card: StoredPaymentMethod) => {
    setCurrentSelectedCard(card)
    if (onSelectCard) {
      onSelectCard(card)
    }
  }

  const handleSaveChanges = () => {
    if (currentSelectedCard && onSelectCard) {
      onSelectCard(currentSelectedCard)
    }
  }

  const displayTitle = t('Saved payment methods')
  const displayEmptyMessage = t('No payment methods saved yet.')

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        backgroundColor: 'background.paper',
        p: selectionMode ? 0 : 3,
        height: '100%',
        flexDirection: 'column',
        display: 'flex',
      }}
    >
      {!selectionMode && (
        <>
          <Typography variant="h4">{displayTitle}</Typography>
          <Divider sx={{ my: 2 }} />
        </>
      )}
      <Stack spacing={2} sx={{ mt: selectionMode ? 0 : 2.5, flex: 1 }}>
        {cards.length > 0 ? (
          <Box
            sx={{
              borderRadius: 1.5,
              border: 1,
              borderColor: theme.palette.grey[200],
              overflow: 'hidden',
            }}
          >
            {cards.map((card: StoredPaymentMethod, index: number) => (
              <React.Fragment key={card.id || index}>
                <CardItem onClick={() => handleCardClick(card)}>
                  <CardLogo>
                    <KCardIcon cardType={card.cardType} />
                  </CardLogo>
                  <Typography sx={{ fontSize: 16, color: '#333', mr: 2.5 }}>
                    •••• •••• •••• {card.bogusCardNum}
                  </Typography>

                  {selectionMode ? (
                    <Radio
                      checked={currentSelectedCard?.id === card.id}
                      onChange={() => handleSelectCard(card)}
                      value={card.id}
                      name="card-selection"
                      color="primary"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ ml: 'auto' }}
                    />
                  ) : (
                    card.default && <DefaultLabel>{t('Default')}</DefaultLabel>
                  )}
                </CardItem>
                {index !== cards.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="secondary">
            {displayEmptyMessage}
          </Typography>
        )}
      </Stack>

      {!hideActionButtons && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: 'flex-end',
            marginTop: 'auto',
            pt: 3,
          }}
        >
          <KButton
            onClick={selectionMode ? handleSaveChanges : handleAddClick}
            disabled={selectionMode && !currentSelectedCard}
            sx={{
              minWidth: 200,
              height: 52,
              borderRadius: 50,
              [theme.breakpoints.down('sm')]: {
                minWidth: 180,
                height: 48,
              },
            }}
          >
            {selectionMode ? t('Save changes') : t('Add New Card')}
          </KButton>
        </Stack>
      )}

      <CreditCardEditDialog
        open={editDialogOpen}
        onClose={handleDialogCancel}
        card={currentSelectedCard}
        isOnlyDefaultCard={isOnlyDefaultCard}
        isNoCard={cards.length === 0}
      />
    </Paper>
  )
}
